import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Skeleton from "@/v2-ui/skeleton"
import ErrorMessage from "@/v2-ui/ErrorMessage"
import { useLayerCategoryQuery } from "@/v2-backoffice-client/layer/category/layer.category.query"
import LayerCategoryLayerAddSelector from "@/v2-backoffice-client/layer/category/layer/add/LayerCategoryLayerAddSelector"
import LayerCategoryLayerRemoveButton from "@/v2-backoffice-client/layer/category/layer/remove/LayerCategoryLayerRemoveButton"

export type LayerCategoryManagerProps = ComponentProps<"div"> & {
  layerFeatureCode: string
  categoryCodes: string[]
  /** Drops the "Categories" heading when the host section supplies its own. */
  isHeadingHidden?: boolean
}

/**
 * The layer's category memberships as a compact wrapped chip row —
 * category name, an "off" marker when the category itself is disabled,
 * and an inline remove. Category order across the chooser is curated
 * globally in the Layers page's Organize dialog, not here.
 */
function LayerCategoryManager(props: LayerCategoryManagerProps) {
  const {
    layerFeatureCode,
    categoryCodes,
    isHeadingHidden = false,
    className,
    ...divProps
  } = props

  const { data, loading, error } = useLayerCategoryQuery()

  const allCategories = data?.getLayerCategories ?? []
  const assignedCategories = allCategories.filter((c) =>
    categoryCodes.includes(c.FeatureCode),
  )

  return (
    <div className={cn("flex flex-col gap-1.5", className)} {...divProps}>
      {!isHeadingHidden && (
        <span className="text-sm font-medium">Categories</span>
      )}

      {loading ? (
        // Skeleton chips in the shape of the loaded row, so the section
        // doesn't jump when the memberships arrive.
        <div className="flex flex-wrap items-center gap-1.5" aria-busy="true">
          <Skeleton className="h-6 w-28 rounded-full" />
          <Skeleton className="h-6 w-20 rounded-full" />
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ) : error ? (
        <ErrorMessage error={error} />
      ) : (
        <div className="flex flex-wrap items-center gap-1.5">
          {assignedCategories.length === 0 && (
            <span className="text-xs text-muted-foreground">
              Not in any category — it won&apos;t show in the console&apos;s
              chooser sections.
            </span>
          )}

          {assignedCategories.map((cat) => (
            <span
              key={cat.FeatureCode}
              className="inline-flex items-center gap-1 rounded-full border bg-muted/40 py-0.5 pl-2.5 pr-0.5 text-xs"
            >
              <span className={cn(!cat.IsEnabled && "text-muted-foreground")}>
                {cat.Name ?? cat.FeatureCode}
              </span>
              {!cat.IsEnabled && (
                <span
                  className="text-[10px] uppercase tracking-wider text-muted-foreground"
                  title="This category is disabled in the console"
                >
                  off
                </span>
              )}
              <LayerCategoryLayerRemoveButton
                layerFeatureCode={layerFeatureCode}
                categoryFeatureCode={cat.FeatureCode}
              />
            </span>
          ))}

          <LayerCategoryLayerAddSelector
            size="xs"
            variant="outline"
            className="rounded-full"
            layerFeatureCode={layerFeatureCode}
            existingCategoryCodes={categoryCodes}
          >
            Add
          </LayerCategoryLayerAddSelector>
        </div>
      )}
    </div>
  )
}

export default LayerCategoryManager
